import { FC, MouseEvent, Suspense } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ArrowRight, Menu } from 'lucide-react';
import GlobalClassFilter from './GlobalClassFilter';
import GlobalClassFilterSync from './GlobalClassFilterSync';
import GlobalSiteFilter from './GlobalSiteFilter';
import GlobalSiteFilterSync from './GlobalSiteFilterSync';
import { isUserAdmin } from '../../businessLogicService/isUserAdmin';
import { getSideNavItems } from './getSideNavItems';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';

interface Props {
  isCollapsed: boolean;
  onToggle: () => void;
}

const SideNav: FC<Props> = ({ isCollapsed, onToggle }) => {
  const userRedux = useAppSelector(selectUser);

  if (!userRedux) return null;

  const items = getSideNavItems(userRedux);
  const isAdmin = isUserAdmin(userRedux);

  const expandOnRailClick = (event: MouseEvent<HTMLElement>) => {
    if (isCollapsed && event.target === event.currentTarget) onToggle();
  };

  return (
    <nav
      className={`nc_side-nav ${isCollapsed ? 'nc_side-nav--collapsed' : ''}`}
      aria-label="Main navigation"
      data-testid="sideNav"
      onClick={expandOnRailClick}
    >
      <div className="nc_side-nav__top">
        <Link to="/" id="logo" aria-label="Teach to One Roadmaps home page" className="nc_side-nav__logo-link">
          <span className="nc_logo" data-testid="logo" />
        </Link>
        <button
          type="button"
          id="sideNavMenuToggle"
          className="nc_side-nav__toggle"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          onClick={onToggle}
        >
          <Menu size={24} aria-hidden="true" />
        </button>
      </div>

      <div className="nc_side-nav__filter">
        <Suspense fallback={<div className="nc_class-filter nc_class-filter--loading" aria-busy="true" />}>
          {isAdmin ? (
            <>
              <GlobalSiteFilter />
              <GlobalSiteFilterSync />
            </>
          ) : (
            <>
              <GlobalClassFilter />
              <GlobalClassFilterSync />
            </>
          )}
        </Suspense>
        {!isAdmin && (
          <NavLink
            to="/classes-rosters"
            id="sideNavSeeAllClassesLink"
            className={({ isActive }) =>
              `nc_side-nav__all-classes ${isActive ? 'nc_side-nav__all-classes--active' : ''}`
            }
          >
            <span className="nc_side-nav__label">See all classes</span>
            <ArrowRight size={16} aria-hidden="true" />
          </NavLink>
        )}
      </div>

      <ul className="nc_side-nav__items">
        {items.map(item => {
          const Icon = item.icon;
          return (
            <li key={item.id}>
              <NavLink
                to={item.to}
                id={item.id}
                end={item.to === '/'}
                title={isCollapsed ? item.label : undefined}
                className={({ isActive }) => `nc_side-nav__item ${isActive ? 'nc_side-nav__item--active' : ''}`}
              >
                <Icon size={20} aria-hidden="true" className="nc_side-nav__icon" />
                <span className="nc_side-nav__label">{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
      <div className="nc_side-nav__powered-by">
        <span className="nc_side-nav__powered-by-text">Powered by</span>
        <span className="nc_side-nav__powered-by-short">By</span>
        <span className="nc_side-nav__powered-by-logo" role="img" aria-label="Teach to One Roadmaps" />
      </div>
    </nav>
  );
};

export default SideNav;
